'use client';

import Image from 'next/image';
import Link from 'next/link';
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import Breadcrumbs from '@/components/Secondary/Breadcrumbs/Breadcrumbs'

interface SecBridgeProps {
	data: {
		content_collection_bridge?: {
			id: string
			site_content_collections_id: {
				title: string
				collection: {
					item: {
						site_content_type?: string
						// Content item properties
						text?: string // from site_content.text
						title?: string // from site_content.title or external_links.title
						url?: string // from external_links.url
						image?: {
							// from site_content.image
							id: string
							title: string
							filename_disk: string
							filename_download: string
						}
						menu_items?: {
							id: string
							label: string
							href: string
							submenu?: {
								id: string
								label: string
								href: string
							}[]
						}[]
						// Menu item properties (direct on item)
						id?: number | string
						label?: string
						href?: string
						submenu?: {
							id: string
							label: string
							href: string
						}[]
						external_links?: {
							id: string
							title: string
							url: string
						}[]
					}
				}[]
			}
		}[]
	}
}

// Define an interface for the image type
interface ImageType {
	id: string;
	title: string;
	filename_disk: string;
	filename_download: string;
}

export const fields = [
	'content_collection_bridge.id',

	'content_collection_bridge.site_content_collections_id.collection.item:site_content.title',
	'content_collection_bridge.site_content_collections_id.collection.item:site_content.text',

	'content_collection_bridge.site_content_collections_id.collection.item:menu_items.id',
	'content_collection_bridge.site_content_collections_id.collection.item:menu_items.label',
	'content_collection_bridge.site_content_collections_id.collection.item:menu_items.href',
	'content_collection_bridge.site_content_collections_id.collection.item:menu_items.submenu.id',
	'content_collection_bridge.site_content_collections_id.collection.item:menu_items.submenu.label',
	'content_collection_bridge.site_content_collections_id.collection.item:menu_items.submenu.href',

	'content_collection_bridge.site_content_collections_id.collection.item:site_content.site_content_type',
	'content_collection_bridge.site_content_collections_id.collection.item:site_content.image.id',
	'content_collection_bridge.site_content_collections_id.collection.item:site_content.image.title',
	'content_collection_bridge.site_content_collections_id.collection.item:site_content.image.filename_disk',
	'content_collection_bridge.site_content_collections_id.collection.item:site_content.image.filename_download',

	'content_collection_bridge.site_content_collections_id.collection.item:external_links.id',
	'content_collection_bridge.site_content_collections_id.collection.item:external_links.title',
	'content_collection_bridge.site_content_collections_id.collection.item:external_links.url',
] as const

export default function SecBridge({ data }: SecBridgeProps) {
	// Ensure content_collection_bridge exists and is an array
	const contentCollectionBridge = data?.content_collection_bridge || [];
	
	return (
		<>
			<Breadcrumbs />
			<section className='SecBridge w-full pb-16'>
				<div className='container max-md:p-0 relative'>
					<div
						className={`flex flex-col ${
							contentCollectionBridge.length === 3
								? 'xl:flex-row items-start'
								: contentCollectionBridge.length === 4
								? 'xl:grid xl:grid-cols-2'
								: 'lg:flex-row'
						} md:gap-16 overflow-hidden`}
					>
						{contentCollectionBridge.map((section, index) => {
							const collection = section.site_content_collections_id.collection
							let title = ''
							let text = ''
							let image: ImageType | null = null
							let menuItems: {
								id: string
								label: string
								href: string
								submenu?: { id: string; label: string; href: string }[]
							}[] = []
							let externalLinks: {
								id: string
								title: string
								url: string
							}[] = []

							// Find title, text, image, menu items and external links from collection items
							collection.forEach(collectionItem => {
								console.log("collectionItem", collectionItem)
								if (
									typeof collectionItem.item === 'object' &&
									collectionItem.item !== null
								) {
									if (collectionItem.item.site_content_type === 'title') {
										title = collectionItem.item.title || ''
									}
									if (collectionItem.item.text) {
										text = collectionItem.item.text
									}
									if (collectionItem.item.image) {
										image = collectionItem.item.image as ImageType
									}
									// Check if this item is a menu item (has id, label, href)
									if (
										collectionItem.item.id &&
										collectionItem.item.label &&
										collectionItem.item.href
									) {
										menuItems.push({
											id: collectionItem.item.id.toString(),
											label: collectionItem.item.label,
											href: collectionItem.item.href,
											submenu: collectionItem.item.submenu,
										})
									}
									// Check if this item has external links (has id, title, url)
									if (
										collectionItem.item.id &&
										collectionItem.item.title &&
										collectionItem.item.url
									) {
										externalLinks.push({
											id: collectionItem.item.id.toString(),
											title: collectionItem.item.title,
											url: collectionItem.item.url,
										})
									}
								}
							})

							return (
								<div
									key={section.id}
									className={`w-full ${
										contentCollectionBridge.length === 3
											? 'xl:flex-1'
											: contentCollectionBridge.length === 4
											? 'xl:flex-1'
											: 'lg:flex-1'
									} bg-[#e0e0e0] ${
										index === 0
											? 'rounded-br-[4rem]'
											: index === contentCollectionBridge.length - 1
											? 'rounded-bl-[4rem]'
											: 'rounded-bl-[4rem] rounded-br-[4rem]'
									} flex flex-col mb-12`}
								>
									{image && (
										<div
											className={`relative w-full h-[17rem] ${
												index === 1 ? 'lg:h-[25rem]' : 'lg:h-[27rem]'
											}`}
										>
											<Image
												src={getReadableImageUrl(
													(image as ImageType).id,
													(image as ImageType).filename_download
												)}
												alt={title || `Section ${index + 1}`}
												fill
												className={`object-cover ${
													index === 0
														? 'md:object-center object-[70%_0]'
														: 'object-center'
												}`}
												sizes='(max-width: 1024px) 100vw, 50vw'
												priority
											/>
											{menuItems.length > 0 && (
												<div className='absolute top-6 left-6 max-lg:text-white max-lg:top-[10px] max-lg:left-6 lg:bottom-6 lg:right-6 lg:left-auto lg:top-auto lg:text-black font-brandon font-medium tracking-wide text-lg'>
													Model
												</div>
											)}
										</div>
									)}
									{title && (
										<div className='px-8 lg:px-16 py-12 lg:py-16 flex flex-col gap-12'>
											<div className='flex flex-col gap-4'>
												<h2 dangerouslySetInnerHTML={sanitizeHTML(title)} />
											</div>

											{text && (
												<div
													className='flex flex-col gap-4 lg:gap-6'
													dangerouslySetInnerHTML={sanitizeHTML(text)}
												/>
											)}
										</div>
									)}

									{menuItems.length > 0 && (
										<div className='px-8 lg:px-12 py-12 lg:py-12 flex flex-col gap-12'>
											{menuItems.map(menuItem => (
												<div key={menuItem.id} className='flex flex-col gap-12'>
													<div className='flex flex-col gap-4'>
														<h2>
															<a href={menuItem.href} className='mb-6'>
																{menuItem.label}
															</a>
														</h2>
														{/* <p className='text-base lg:text-lg uppercase tracking-[0.2em]'>
															Procedures
														</p> */}
													</div>
													{menuItem.submenu && menuItem.submenu.length > 0 && (
														<div className='flex flex-col gap-4 lg:gap-6'>
															{menuItem.submenu.map(subItem => (
																<a
																	key={subItem.id}
																	href={subItem.href}
																	className='text-xl uppercase tracking-[0.15em] hover:text-primary transition-colors'
																>
																	{subItem.label}
																</a>
															))}
														</div>
													)}
												</div>
											))}
										</div>
									)}

									{externalLinks.length > 0 &&
										externalLinks.map(link => (
											<div
												key={link.id}
												className='px-8 lg:px-16 pb-12 lg:pb-16'
											>
												<Link href={link.url} className='btn'>
													{link.title}
												</Link>
											</div>
										))}
								</div>
							)
						})}
					</div>
				</div>
			</section>
		</>
	)
}